import apiClient from "./middleware";

export interface DSRReport {
  id: string;
  fileName: string;
  fileSize: string;
  downloadUrl: string;
  createdAt: string;
  status: "available" | "generating" | "failed";
  recordCount: number;
  reportType: "genent" | "music-videos" | "sound-recordings";
  sftpUploadedAt?: string;
  sftpRemotePath?: string;
  sftpUploadedBy?: string;
}

export interface SFTPRemoteFile {
  name: string;
  size: number;
  modifyTime: number;
  type: "file" | "directory" | "other";
  path: string;
}

export interface SFTPStatusResponse {
  connected: boolean;
  host: string;
  port: number;
  username: string;
  cwd: string;
  message: string;
}

export interface SFTPListFilesResponse {
  path: string;
  files: SFTPRemoteFile[];
}

export interface UploadReportToSFTPResult {
  reportId: string;
  fileName: string;
  remotePath: string;
  success: boolean;
  error?: string;
}

export interface UploadReportsToSFTPResponse {
  message: string;
  results: UploadReportToSFTPResult[];
}

export interface UploadManualFileToSFTPResponse {
  message: string;
  remotePath: string;
  fileName: string;
  reportKind?: string;
  columnsValidated?: string[];
}

export interface DeleteSftpFileResponse {
  message: string;
  remotePath: string;
  fileName: string;
}

export interface GenerateReportRequest {
  reportType: "genent" | "music-videos" | "sound-recordings";
}

export interface GenerateReportResponse {
  reportId: string;
  status: "queued" | "failed";
  message: string;
}

export interface AuditQueueRecord {
  id?: string;
  reportId: string;
  reportType: "genent" | "music-videos" | "sound-recordings";
  generatedAt?: string;
  missingFields: string[];
  unverifiedEntities?: SnapshotEntityIssue[];
  validationIssues?: string[];
  reviewStatus?: "pending" | "approved" | "rejected";
  AssetID?: string;
  SoundRecordingId?: string;
  CompositionAssetId?: string;
  ISRC?: string;
  AssetType?: string;
  Artist?: string;
  SongTitle?: string;
  Label?: string;
  Publisher?: string;
}

export type SnapshotEntityType = "artist" | "label" | "publisher";

export interface SnapshotEntityIssue {
  entityType: SnapshotEntityType;
  name: string;
}

export type SnapshotArtistStatus = "accepted" | "rejected";

export interface SnapshotEntity {
  id: string;
  entityType: SnapshotEntityType;
  name: string;
  normalizedName: string;
  createdAt: string;
  createdBy?: string;
  status?: SnapshotArtistStatus;
  statusUpdatedAt?: string;
  statusUpdatedBy?: string;
}

export interface ArtistStatusLogEntry {
  id: string;
  artistName: string;
  normalizedName: string;
  previousStatus: SnapshotArtistStatus | null;
  newStatus: SnapshotArtistStatus;
  changedBy: string;
  changedAt: string;
  source: "master_list" | "audit_queue";
  auditQueueId?: string;
}

export type DSRDashboardStatus = "healthy" | "warning" | "critical" | "info";

export interface DSRGenerationBreakdown {
  missingRequiredFieldRecords: number;
  unverifiedEntityRecords: number;
  validationIssueRecords: number;
}

export interface DSRDashboardMetric {
  reportType: "genent" | "music-videos" | "sound-recordings";
  latestReportAt: string | null;
  latestReportAgeDays: number | null;
  totalReports: number;
  availableReports: number;
  availabilityRate: number;
  latestQueueTotal: number;
  latestQueueCleanRecords: number;
  latestQueueIssueRecords: number;
  pendingReviewCount: number;
  reportRowCount: number;
  totalRowsEvaluated: number;
  inclusionRatePercent: number;
  /** Same as inclusionRatePercent; kept for compatibility. */
  healthScore: number;
  status: DSRDashboardStatus;
  stale: boolean;
  generationBreakdown?: DSRGenerationBreakdown;
}

export interface DSRDashboardMetricsResponse {
  genent: DSRDashboardMetric;
  "music-videos": DSRDashboardMetric;
  "sound-recordings": DSRDashboardMetric;
}

export const dsrAuditsApi = {
  // Get all reports for a specific type
  getReports: async (
    reportType: "genent" | "music-videos" | "sound-recordings"
  ): Promise<DSRReport[]> => {
    try {
      const response = await apiClient.get(`/dsr-audits/reports/${reportType}`);
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  // Generate a new report
  generateReport: async (
    request: GenerateReportRequest
  ): Promise<GenerateReportResponse> => {
    try {
      const response = await apiClient.post("/dsr-audits/generate", request);
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  // Download a specific report
  downloadReport: async (reportId: string): Promise<void> => {
    try {
      console.log(`[DEBUG] Starting download for report ID: ${reportId}`);

      // Make an authenticated request to get the file
      const response = await apiClient.get(`/dsr-audits/download/${reportId}`, {
        responseType: "blob",
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"]?.toString() ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download =
        response.headers["content-disposition"]
          ?.toString()
          .split("filename=")[1]
          ?.replace(/"/g, "") || `report_${reportId}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      window.URL.revokeObjectURL(url);

      console.log(`[DEBUG] Download completed for report ID: ${reportId}`);
    } catch (error) {
      console.error(`[DEBUG] Error in downloadReport:`, error);
      throw error;
    }
  },

  // Alternative download method using blob
  downloadReportAsBlob: async (reportId: string): Promise<void> => {
    try {
      console.log(`[DEBUG] Starting blob download for report ID: ${reportId}`);

      // Get the authorization token
      const token = localStorage.getItem("firebaseToken");

      // Fetch the file as a blob
      const response = await fetch(
        `${apiClient.defaults.baseURL}/dsr-audits/download/${reportId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(
          `Download failed: ${response.status} ${response.statusText}`
        );
      }

      // Get the blob from the response
      const blob = await response.blob();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `report_${reportId}.xlsx`; // Default filename

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(url);

      console.log(`[DEBUG] Blob download completed for report ID: ${reportId}`);
    } catch (error) {
      console.error(`[DEBUG] Error in downloadReportAsBlob:`, error);
      throw error;
    }
  },

  // Get report status
  getReportStatus: async (
    reportId: string
  ): Promise<{ status: string; progress?: number; message?: string }> => {
    try {
      const response = await apiClient.get(`/dsr-audits/status/${reportId}`);
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  // Get all reports across all types
  getAllReports: async (): Promise<DSRReport[]> => {
    try {
      const response = await apiClient.get("/dsr-audits/reports");
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  // Get backend-calculated dashboard metrics for all report types
  getDashboardMetrics: async (): Promise<DSRDashboardMetricsResponse> => {
    try {
      const response = await apiClient.get("/dsr-audits/dashboard-metrics");
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  // Get latest audit queue records by report type
  getLatestAuditQueue: async (
    reportType: "genent" | "music-videos" | "sound-recordings",
    options?: { includeRejected?: boolean }
  ): Promise<AuditQueueRecord[]> => {
    try {
      const query = options?.includeRejected ? "?includeRejected=true" : "";
      const response = await apiClient.get(
        `/dsr-audits/audit-queue/latest/${reportType}${query}`
      );
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  // Trigger report generation (for Cloud Scheduler)
  triggerReportGeneration: async (
    request: GenerateReportRequest
  ): Promise<GenerateReportResponse> => {
    try {
      const response = await apiClient.post("/dsr-audits/trigger", request, {
        headers: {
          "X-CloudScheduler": "true",
        },
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  // Delete a specific report
  deleteReport: async (reportId: string): Promise<void> => {
    try {
      await apiClient.delete(`/dsr-audits/reports/${reportId}`);
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  // Update audit queue review status
  updateAuditQueueStatus: async (
    auditQueueId: string,
    status: "pending" | "approved" | "rejected"
  ): Promise<void> => {
    try {
      await apiClient.post(`/dsr-audits/audit-queue/${auditQueueId}/status`, {
        status,
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  // Snapshot: list master artists
  getArtistSnapshots: async (options?: {
    status?: SnapshotArtistStatus;
  }): Promise<SnapshotEntity[]> => {
    try {
      const query = options?.status ? `?status=${options.status}` : "";
      const response = await apiClient.get(
        `/dsr-audits/snapshots/artists${query}`
      );
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  // Snapshot: add or re-accept master artist
  addArtistSnapshot: async (name: string): Promise<SnapshotEntity> => {
    try {
      const response = await apiClient.post("/dsr-audits/snapshots/artists", {
        name,
      });
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  // Snapshot: accept or reject master artist
  updateArtistSnapshotStatus: async (
    artistId: string,
    status: SnapshotArtistStatus
  ): Promise<SnapshotEntity> => {
    try {
      const response = await apiClient.patch(
        `/dsr-audits/snapshots/artists/${artistId}/status`,
        { status }
      );
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  // Snapshot: status change audit trail
  getArtistStatusHistory: async (limit = 100): Promise<ArtistStatusLogEntry[]> => {
    try {
      const response = await apiClient.get(
        `/dsr-audits/snapshots/artists/history?limit=${limit}`
      );
      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },

  getSftpStatus: async (): Promise<SFTPStatusResponse> => {
    const response = await apiClient.get("/dsr-audits/sftp/status");
    return response.data;
  },

  listSftpFiles: async (path = "/"): Promise<SFTPListFilesResponse> => {
    const response = await apiClient.get("/dsr-audits/sftp/files", {
      params: { path },
    });
    return response.data;
  },

  uploadReportsToSftp: async (
    reportIds: string[],
    accepted: boolean
  ): Promise<UploadReportsToSFTPResponse> => {
    const response = await apiClient.post("/dsr-audits/sftp/upload/reports", {
      reportIds,
      accepted,
    });
    return response.data;
  },

  uploadManualFileToSftp: async (
    file: File
  ): Promise<UploadManualFileToSFTPResponse> => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await apiClient.post(
      "/dsr-audits/sftp/upload/file",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    return response.data;
  },

  deleteSftpFile: async (fileName: string): Promise<DeleteSftpFileResponse> => {
    const response = await apiClient.delete("/dsr-audits/sftp/files", {
      data: { fileName },
    });
    return response.data;
  },
};
